/**
 * Страница по временной ссылке.
 *
 * Её открывает человек со стороны: заказчик, которому обещали доставку, или
 * механик, которого послали к машине. Учётной записи у него нет и не будет.
 *
 * Поэтому здесь нет ничего, кроме одной машины на карте. Ни списка парка, ни
 * истории, ни кнопок — ссылка на одну машину открывает ровно одну машину.
 * Состояние реле сюда тоже не приходит: постороннему знать, заблокирована ли
 * машина, незачем.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import maplibregl, { type Map as MapLibreMap, Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

import { ApiError, api, knotsToKmh, type SharedVehicle } from './api'
import { dateTime } from './format'
import { EtaButton } from './EtaButton'
import { RouteButton } from './RouteButton'
import { dict, type Lang } from './i18n'

const STYLE_URL = 'https://tiles.versatiles.org/assets/styles/colorful/style.json'

/** Как часто обновлять точку. Реже, чем в приложении: это не диспетчерская. */
const REFRESH_MS = 15_000

const TRUCK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
  stroke-linecap="round" stroke-linejoin="round">
  <path d="M2 16V7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9"/>
  <path d="M14 10h3.6a1 1 0 0 1 .82.43l2.4 3.4a1 1 0 0 1 .18.57V16"/>
  <circle cx="7" cy="17.5" r="1.9"/><circle cx="17.5" cy="17.5" r="1.9"/>
  <path d="M9 17.5h6.5M2 16h3"/></svg>`

export function Shared({
  shareKey,
  lang,
  maps,
}: {
  shareKey: string
  lang: Lang
  /** Заведён ли ключ Google: без него времени в пути нет. */
  maps: boolean
}) {
  const t = dict(lang)
  const [data, setData] = useState<SharedVehicle | null>(null)
  const [gone, setGone] = useState(false)
  const [routeOpen, setRouteOpen] = useState(false)

  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const marker = useRef<Marker | null>(null)
  const centred = useRef(false)

  const load = useCallback(async () => {
    try {
      setData(await api.shared(shareKey))
    } catch (caught) {
      // Ссылку могли отозвать или её срок вышел — это не сбой, а норма.
      if (caught instanceof ApiError && caught.status === 404) setGone(true)
    }
  }, [shareKey])

  useEffect(() => {
    void load()
    const timer = setInterval(() => void load(), REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  useEffect(() => {
    if (!container.current || map.current) return
    map.current = new maplibregl.Map({
      container: container.current,
      style: STYLE_URL,
      center: [13.405, 52.52],
      zoom: 11,
      attributionControl: { compact: true },
    })
    return () => {
      map.current?.remove()
      map.current = null
      marker.current = null
    }
  }, [])

  useEffect(() => {
    const m = map.current
    const point = data?.position
    if (!m || !point) return

    const at: [number, number] = [point.longitude, point.latitude]
    if (!marker.current) {
      const element = document.createElement('span')
      element.className = 'pin'
      const body = document.createElement('span')
      body.className = 'pin-body'
      body.innerHTML = TRUCK_SVG
      element.append(body)
      element.dataset.st = 'move'
      // Нажатие на саму машинку открывает выбор карт — так это устроено в
      // мессенджерах, и человек со стороны ищет маршрут именно там, а не в
      // подписи под картой.
      element.addEventListener('click', () => setRouteOpen(true))
      element.style.cursor = 'pointer'
      marker.current = new maplibregl.Marker({ element }).setLngLat(at).addTo(m)
    } else {
      marker.current.setLngLat(at)
    }

    // Наводим карту один раз: дальше человек волен её двигать, и вырывать
    // её из-под пальца при каждом обновлении нельзя.
    if (!centred.current) {
      centred.current = true
      m.easeTo({ center: at, zoom: 14, duration: 0 })
    }
  }, [data])

  if (gone) {
    // key обязателен. Без него React видит на этом месте такой же div, что и
    // у карты, и просто меняет класс: очистка не срабатывает, а MapLibre
    // остаётся внутри вместе с живым контекстом WebGL — под сообщением о том,
    // что ссылка недействительна.
    return (
      <div className="share-gone" key="gone">
        <p>{t.shareGone}</p>
      </div>
    )
  }

  return (
    <>
      <div className="map" ref={container} key="map" />
      <div className="share-card">
        <h1>{data?.deviceName ?? t.loading}</h1>
        {data?.position ? (
          <p>
            {knotsToKmh(data.position.speed)} {t.speed}
            {data.position.deviceTime && (
              <> · {dateTime(data.position.deviceTime, lang)}</>
            )}
          </p>
        ) : (
          // Положения нет — нейтрально, без «нет связи» (решение владельца 28.09).
          data && <p>{t.quiet}</p>
        )}
        {data && <p className="share-until">{t.shareUntil} {dateTime(data.expiresAt, lang, true)}</p>}
        {maps && data?.position && (
          <EtaButton lang={lang} ask={(from) => api.sharedEta(shareKey, from)} />
        )}
        <RouteButton
          latitude={data?.position?.latitude ?? null}
          longitude={data?.position?.longitude ?? null}
          label={data?.deviceName ?? ''}
          lang={lang}
          open={routeOpen}
          onOpen={() => setRouteOpen(true)}
          onClose={() => setRouteOpen(false)}
        />
      </div>
    </>
  )
}
